import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Image,
  Slider,
  SliderFilledTrack,
  SliderThumb,
  SliderTrack,
  Stack,
  Text,
  useToast,
} from '@chakra-ui/react';
import Cropper, { Area, Point } from 'react-easy-crop';
import { getCroppedImageFile } from './cropImage';
import { colors } from '../../utils/colors';

export interface ImageCropFieldProps {
  /** Ancho de salida exacto que se envía al back (px). */
  outputWidth: number;
  /** Alto de salida exacto que se envía al back (px). */
  outputHeight: number;
  /** Nombre del archivo recortado resultante. */
  fileName: string;
  /** Imagen ya guardada (edición). Se muestra hasta que el usuario suba una nueva. */
  existingUrl?: string;
  /** Tamaño máximo del archivo de origen, en MB. */
  maxFileSizeMB?: number;
  /** Devuelve el File recortado (o null al quitar la imagen). */
  onChange: (file: File | null) => void;
}

const ImageCropField: React.FC<ImageCropFieldProps> = ({
  outputWidth,
  outputHeight,
  fileName,
  existingUrl,
  maxFileSizeMB = 50,
  onChange,
}) => {
  const toast = useToast();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [isDragging, setIsDragging] = useState<boolean>(false);

  const aspect = outputWidth / outputHeight;

  // Limpia la object URL al desmontar o al cambiar de origen.
  useEffect(() => {
    return () => {
      if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    };
  }, [sourceUrl]);

  const onCropComplete = useCallback((_: Area, areaPixels: Area) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  // Cada vez que cambia el encuadre, genera el File recortado y lo entrega.
  useEffect(() => {
    let cancelled = false;
    if (!sourceUrl || !croppedAreaPixels) return undefined;

    getCroppedImageFile(
      sourceUrl,
      croppedAreaPixels,
      outputWidth,
      outputHeight,
      fileName
    )
      .then(file => {
        if (!cancelled) onChange(file);
      })
      .catch(() => {
        /* noop: si falla el recorte no se actualiza el valor */
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [croppedAreaPixels, sourceUrl, outputWidth, outputHeight, fileName]);

  const handleFile = (file?: File | null) => {
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Archivo inválido',
        description: 'Debe ser una imagen',
        status: 'error',
        duration: 2500,
        position: 'top-right',
      });
      return;
    }

    if (file.size > maxFileSizeMB * 1024 * 1024) {
      toast({
        title: 'Archivo demasiado grande',
        description: `Tamaño máximo: ${maxFileSizeMB}MB`,
        status: 'error',
        duration: 2500,
        position: 'top-right',
      });
      return;
    }

    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setSourceUrl(URL.createObjectURL(file));
  };

  const handleRemove = () => {
    if (sourceUrl) URL.revokeObjectURL(sourceUrl);
    setSourceUrl(null);
    setCroppedAreaPixels(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    if (inputRef.current) inputRef.current.value = '';
    onChange(null);
  };

  const openPicker = () => inputRef.current?.click();

  const hiddenInput = (
    <input
      ref={inputRef}
      type="file"
      accept="image/*"
      style={{ display: 'none' }}
      onChange={e => handleFile(e.target.files?.[0])}
    />
  );

  // --- Estado: encuadrando una imagen nueva ---
  if (sourceUrl) {
    return (
      <Box>
        {hiddenInput}
        <Box
          pos="relative"
          w="100%"
          h="300px"
          borderRadius="12px"
          overflow="hidden"
          bg="#1A1726">
          <Cropper
            image={sourceUrl}
            crop={crop}
            zoom={zoom}
            aspect={aspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={onCropComplete}
            objectFit="contain"
            showGrid
          />
        </Box>
        <Stack
          direction="row"
          align="center"
          spacing={4}
          mt={3}
          px={1}>
          <Text fontSize="sm" color={colors.gray[0]} minW="48px">
            Zoom
          </Text>
          <Slider
            aria-label="zoom"
            min={1}
            max={3}
            step={0.01}
            value={zoom}
            onChange={setZoom}
            colorScheme="yellow"
            flex={1}>
            <SliderTrack>
              <SliderFilledTrack />
            </SliderTrack>
            <SliderThumb />
          </Slider>
          <Button
            size="sm"
            variant="outline"
            color={colors.gray[0]}
            onClick={handleRemove}>
            Cambiar imagen
          </Button>
        </Stack>
        <Text fontSize="xs" color="#A7A5B8" mt={2} px={1}>
          Arrastrá la imagen y usá el zoom para elegir la porción que se va a
          ver ({outputWidth}×{outputHeight}).
        </Text>
      </Box>
    );
  }

  // --- Estado: imagen ya guardada (edición) sin cambios ---
  if (existingUrl) {
    return (
      <Box>
        {hiddenInput}
        <Box
          pos="relative"
          w="100%"
          borderRadius="12px"
          overflow="hidden"
          bg="#1A1726"
          maxW={`${Math.min(outputWidth, 480)}px`}>
          <Image
            src={existingUrl}
            alt="Imagen actual"
            w="100%"
            objectFit="cover"
          />
        </Box>
        <Button
          size="sm"
          variant="outline"
          color={colors.gray[0]}
          mt={3}
          onClick={openPicker}>
          Cambiar imagen
        </Button>
      </Box>
    );
  }

  // --- Estado: vacío (dropzone) ---
  return (
    <Box>
      {hiddenInput}
      <Box
        role="button"
        onClick={openPicker}
        onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={e => {
          e.preventDefault();
          setIsDragging(false);
          handleFile(e.dataTransfer.files?.[0]);
        }}
        cursor="pointer"
        w="100%"
        minH="160px"
        display="flex"
        flexDir="column"
        justifyContent="center"
        alignItems="center"
        textAlign="center"
        px={4}
        borderRadius="12px"
        borderWidth="2px"
        borderStyle="dashed"
        borderColor={isDragging ? colors.accent.main : '#4A4763'}
        bg={isDragging ? 'rgba(255,199,0,0.08)' : 'rgba(255,255,255,0.03)'}
        transition="all 0.15s ease">
        <Text color={colors.gray[0]} fontSize="sm">
          Arrastre la imagen o{' '}
          <Text as="span" color={colors.accent.main} fontWeight="700">
            elíjala desde archivo
          </Text>
        </Text>
        <Text color="#A7A5B8" fontSize="xs" mt={1}>
          Se recortará a {outputWidth}×{outputHeight}
        </Text>
      </Box>
    </Box>
  );
};

export default ImageCropField;
